import axios from "axios";
import { sanitizePayload } from "./sanitizePayload";
import { ROUTES } from "../shared/utils";

declare module "axios" {
  interface AxiosRequestConfig<D = any> {
    /** When true, a 403 response is returned to the caller instead of redirecting to login. A 401 (expired session) still redirects. */
    skipForbiddenRedirect?: boolean;
  }
}

const API_BASE_URL = import.meta.env.VITE_APPLICATION_API || "https://localhost:8443";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
});

/* REQUEST INTERCEPTOR — SANITIZE ALL PAYLOADS */
apiClient.interceptors.request.use(
  (config) => {
    if (config.data) {
      config.data = sanitizePayload(config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function isAuthRequest(url: string): boolean {
  if (!url) return false;

  const authEndpoints = [
    '/auth',
    '/auth/login',
    '/auth/logout',
    '/auth/forgot-password',
    '/auth/confirm-forgot-password',
    '/auth/is-authenticated',
    '/auth/current-user'
  ];
  return authEndpoints.some((endpoint) => url.includes(endpoint));
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthRequestCall = isAuthRequest(error.config?.url);
    if (error.response?.status === 401) {
      if (!isAuthRequestCall) {
        window.location.href = ROUTES.LOGIN + "?message=Session expired. Please log in again.";
      }
    }
    if (error.response?.status === 403) {
      if (!isAuthRequestCall && !error.config?.skipForbiddenRedirect) {
        window.location.href = `${ROUTES.LOGIN}?message=Unauthorized. Please log in again.&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;